import React, { useEffect }  from 'react';
import Grid from '@material-ui/core/Grid';
import Alert from '@material-ui/lab/Alert';
import Typography from '@material-ui/core/Typography';
import Input from 'components/form/Input';
import PhoneInput from 'components/form/PhoneInput';
import Select from 'components/form/Select';
import TIMEZONES from 'constant/timezones';
import Box from '@material-ui/core/Box';
import Button from 'components/common/Button';
import Form from 'components/form';
import { makeStyles } from '@material-ui/styles';
import useSnack from 'hooks/useSnack';
import { validatePhone } from 'utils';
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import Divider from './CustomDivider';

const FormItem = Form.FormItem;
const CONTRACT_URL = `https://weadmit-static.s3.us-east-2.amazonaws.com/contracts/`;

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiFormLabel-root': {
      color: '#333',
    },
  },
  sectionTitle: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(3),
  },
}));

const normalizeEmail =  (v, prev) => {
  if (v === prev) {
    return v;
  }
  return v.toLowerCase().trim();
}


const CustomerInfoForm = ({ form, values, step, onSubmit, onNext, onBack, skipParents }) => {
  const classes = useStyles();
  const { getFieldDecorator, validateFields } = form;
  const snack = useSnack();

  useEffect(() => {
    if (step === 1 && skipParents) {
      onNext();
    }
  }, [step]);

  const handleSubmit = () => {
    validateFields().then(
      async (vals) => {
        let goNext = true;
        if (!skipParents && vals.parent.emailConfirm !== vals.parent.email) {
          snack.error('Please verify parent email');
        } else if(step === 2 && vals.student.email !== vals.student.emailConfirm) {
          snack.error('Please verify student email');
        } else if(step === 2 && !skipParents) {
          if (values.parent.email === vals.student.email) {
            snack.error('Parent and Student email cannot be same');
            goNext = false;
          } else if (values.parent.phone === vals.student.phone) {
            snack.error('Parent and Student phone number cannot be same');
            goNext = false;
          } else {
            onSubmit(vals);
            return onNext();
          }
        }
        onSubmit(vals);
        if (goNext) {
          onNext();
        }
      },
      (e) => {
        snack.error('Please validate your inputs');
      },
    );
  };

  return (
    <Form className={classes.root}>
      {/* <Divider /> */}
      { step === 1 && !skipParents && (
        <Grid container>
          <Grid item md={12}>
            <Typography align='center' variant="h3" className={classes.sectionTitle}>
              Add Parent/Guardian Info
            </Typography>
            {/* <Box marginBottom={3}>
              <Typography color="primary" component="span">☑</Typography> All fields are required
            </Box> */}
          </Grid>
          <Grid item  xs={12}>
            <FormItem>
              {getFieldDecorator('parent.firstName', {
                initialValue: values.parent.firstName,
                rules: [
                  {
                    required: true,
                    message: 'Please enter your first name',
                  },
                ],
              })(<Input label="First Name" />)}
            </FormItem>
          </Grid>
          <Grid item xs={12}>
            <FormItem>
              {getFieldDecorator('parent.lastName', {
                initialValue: values.parent.lastName,
                rules: [
                  {
                    required: true,
                    message: 'Please enter your last name',
                  },
                ],
              })(<Input label="Last Name" />)}
            </FormItem>
          </Grid>
          <Grid item xs={12}>
            <FormItem>
              {getFieldDecorator('parent.email', {
                initialValue: values.parent.email,
                normalize: normalizeEmail,
                rules: [
                  {
                    required: true,
                    message: 'Please enter your email address',
                  },
                  {
                    type: 'email',
                    message: 'Please enter a valid email',
                  },
                ],
              })(
                <Input
                  label="Parent/Guardian Email"
                  type="email"
                  hint="We'll create a Guardian account for you and send updates"
                />,
              )}
            </FormItem>
          </Grid>
          <Grid item xs={12}>
            <FormItem>
              {getFieldDecorator('parent.emailConfirm', {
                initialValue: values.parent.emailConfirm,
                normalize: normalizeEmail,
                rules: [
                  {
                    required: true,
                    message: 'Please confirm your email address',
                  },
                  {
                    type: 'email',
                    message: 'Please enter a valid email',
                  },
                ],
              })(
                <Input
                  label="Confirm Email"
                  type="email"
                  hint="Please enter your email address again"
                />,
              )}
            </FormItem>
          </Grid>
          <Grid item xs={12}>
            <FormItem>
              {getFieldDecorator('parent.phone', {
                initialValue: values.parent.phone,
                rules: [
                  {
                    required: true,
                    message: 'Please enter your phone number',
                  },
                  {
                    validator: validatePhone,
                    skipNetwork: true,
                  },
                ],
              })(
                <PhoneInput
                  label="Parent/Guardian Mobile Number"
                  hint="We'll send text updates to your mobile number"
                />,
              )}
            </FormItem>
          </Grid>
          <Grid item xs={12}>
            <FormItem>
              {getFieldDecorator('parent.address1', {
                initialValue: values.parent.address1,
                rules: [
                  {
                    required: true,
                    message: 'Please enter your address',
                  },
                ],
              })(<Input label="Address" hint="Your home address" />)}
            </FormItem>
          </Grid>
          <Grid item xs={12}>
            <FormItem>
              {getFieldDecorator('parent.city', {
                initialValue: values.parent.city,
                rules: [
                  {
                    required: true,
                    message: 'Please enter city',
                  },
                ],
              })(<Input label="City" />)}
            </FormItem>
          </Grid>
          <Grid item xs={6}>
            <FormItem>
              {getFieldDecorator('parent.state', {
                initialValue: values.parent.state,
                rules: [
                  {
                    required: true,
                    message: 'Please enter state',
                  },
                ],
              })(<Input label="State" />)}
            </FormItem>
          </Grid>
          <Grid item xs={6}>
            <FormItem>
              {getFieldDecorator('parent.zip', {
                initialValue: values.parent.zip,
                rules: [
                  {
                    required: true,
                    message: 'Please enter zip code',
                  },
                ],
              })(<Input label="Zip code" type="number" />)}
            </FormItem>
          </Grid>
        </Grid>
      )}
      { step === 2 && (
        <Box>
          <Typography align='center' variant="h3" className={classes.sectionTitle}>
            Student Details
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormItem>
                {getFieldDecorator('student.firstName', {
                  initialValue: values.student.firstName,
                  rules: [
                    {
                      required: true,
                      message: 'Please enter your first name',
                    },
                  ],
                })(<Input label="Student First Name" />)}
              </FormItem>
            </Grid>
            <Grid item xs={12}>
              <FormItem>
                {getFieldDecorator('student.lastName', {
                  initialValue: values.student.lastName,
                  rules: [
                    {
                      required: true,
                      message: 'Please enter your last name',
                    },
                  ],
                })(<Input label="Student Last Name" />)}
              </FormItem>
            </Grid>
            <Grid item xs={12}>
              <FormItem>
                {getFieldDecorator('student.email', {
                  initialValue: values.student.email,
                  normalize: normalizeEmail,
                  rules: [
                    {
                      required: true,
                      message: 'Please enter your email address',
                    },
                    {
                      type: 'email',
                      message: 'Please enter a valid email',
                    },
                  ],
                })(
                  <Input
                    type="email"
                    label="Student Email"
                    hint="We'll create a Student account and send updates"
                  />,
                )}
              </FormItem>
            </Grid>
            <Grid item xs={12}>
              <FormItem>
                {getFieldDecorator('student.emailConfirm', {
                  initialValue: values.student.emailConfirm,
                  normalize: normalizeEmail,
                  rules: [
                    {
                      required: true,
                      message: 'Please confirm your email address',
                    },
                    {
                      type: 'email',
                      message: 'Please enter a valid email',
                    },
                  ],
                })(
                  <Input
                    type="email"
                    label="Confirm Email"
                    hint="Please enter student's email address again"
                  />,
                )}
              </FormItem>
            </Grid>
            <Grid item xs={12}>
              <FormItem>
                {getFieldDecorator('student.phone', {
                  initialValue: values.student.phone,
                  rules: [
                    {
                      required: true,
                      message: 'Please enter your phone number',
                    },
                    {
                      validator: validatePhone,
                      skipNetwork: true,
                    },
                  ],
                })(
                  <PhoneInput
                    label="Student Mobile Number"
                    hint="We will send text updates to student’s mobile number"
                  />,
                )}
              </FormItem>
            </Grid>
            {!skipParents && (
              <Grid item xs={12}>
                <FormItem>
                  {getFieldDecorator('parent.relationship', {
                    initialValue: values.parent.relationship,
                    rules: [
                      {
                        required: true,
                        message: 'Please enter relationship',
                      },
                    ],
                  })(<Input label="What is your relationship with the student?" />)}
                </FormItem>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormItem>
                {getFieldDecorator('timezone', {
                  initialValue: values.timezone,
                  rules: [{ required: true, message: 'Please select timezone' }],
                })(
                  <Select
                    noMargin
                    transformValue
                    isMulti={false}
                    label="Student's Timezone?"
                    options={TIMEZONES}
                  />,
                )}
              </FormItem>
            </Grid>
          </Grid>
        </Box>
      )}
      <Box marginTop={5} display="flex" justifyContent="space-between">
        <Button
          size="large"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}>Back</Button>
        <Button
          endIcon={<ArrowForwardIcon />}
          size="large"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          Next
        </Button>
      </Box>
    </Form>
  );
};

export default Form.create()(CustomerInfoForm);
